import React, { useEffect, useRef } from 'react';
import { Check, CircleDot } from 'lucide-react';
import type { Step } from '../types';

interface StepIndicatorProps {
  currentStep: Step;
  steps: { id: Step; label: string }[];
}

export function StepIndicator({ currentStep, steps }: StepIndicatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to top when step changes
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Center the current step in the viewport if it's outside the visible area
    if (currentStepRef.current && containerRef.current) {
      const container = containerRef.current;
      const currentStepElement = currentStepRef.current;
      
      const containerWidth = container.offsetWidth;
      const stepPosition = currentStepElement.offsetLeft;
      const stepWidth = currentStepElement.offsetWidth;
      
      // Calculate the center position
      const targetScrollPosition = stepPosition - (containerWidth / 2) + (stepWidth / 2);
      
      container.scrollTo({
        left: targetScrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  
  // Use the steps as provided (already filtered by parent component)
  const filteredSteps = steps;

  return (
    <div className="sticky top-0 bg-white z-50 shadow-sm">
      <div 
        ref={containerRef}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto scrollbar-hide"
      >
        <div className="flex items-center min-w-max">
          {filteredSteps.map((step, index) => {
            // Find the original index in the unfiltered steps array
            const originalIndex = steps.findIndex(s => s.id === step.id);
            // Determine if this step is completed based on the original index
            const isCompleted = originalIndex < currentStepIndex;
            
            return (
              <React.Fragment key={step.id}>
                <div 
                  ref={step.id === currentStep ? currentStepRef : null}
                  className={`flex items-center ${
                    index === filteredSteps.length - 1 ? 'flex-shrink-0' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`rounded-full h-8 w-8 flex items-center justify-center transition-all ${
                        currentStep === step.id
                          ? 'bg-blue-600 text-white'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <CircleDot className="h-5 w-5" />
                      )}
                    </div>
                    <span 
                      className={`ml-2 text-sm font-medium transition-colors ${
                        currentStep === step.id
                          ? 'text-blue-600'
                          : isCompleted
                          ? 'text-green-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                </div>
                {index < filteredSteps.length - 1 && (
                  <div 
                    className={`flex-1 mx-2 sm:mx-4 h-0.5 min-w-[1rem] transition-colors ${
                      isCompleted
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}